import { Component, ElementRef, HostListener, OnDestroy, OnInit, ChangeDetectionStrategy, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { takeUntil } from 'rxjs';
import { AuthService } from 'src/app/service/auth.service';
import { StorageService } from 'src/app/service/storage.service';
import { LanguageService } from 'src/app/service/language.service';
import { TranslationService } from 'src/app/service/translation.service';
import { CommonService } from 'src/app/service/common.service';
import { Subject } from 'rxjs';

import { ViewToggleComponent } from 'src/app/components/view-toggle/view-toggle.component';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: true,
    imports: [CommonModule, ViewToggleComponent],
})
export class HeaderComponent implements OnInit, OnDestroy {
  private commonService = inject(CommonService);
  isOpen = false;
  currentLanguage = signal('en');
  isMobile = toSignal(this.commonService.isMobile$, { initialValue: false });
  sidebarMenuOpen = toSignal(this.commonService.sidebarOpen$, { initialValue: true });
  isLoggedIn = signal(false);
  userName = '';
  userSubType = '';
  private destroy$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private elementRef: ElementRef,
    private storage: StorageService,
    private languageService: LanguageService,
    public translationService: TranslationService,
    private router: Router
  ){
    this.storage.getItem('local', 'authData').subscribe((userData: any) => {
      if(userData){
        this.userSubType = userData.sub_type;
        this.isLoggedIn.set(true);
        this.userName = userData.name;
      } else {
        this.isLoggedIn.set(false);
      }
    });
  }

  ngOnInit(): void {
    this.currentLanguage.set(this.languageService.current || 'en');
    this.translationService.currentLang$
      .pipe(takeUntil(this.destroy$))
      .subscribe((language) => {
        this.currentLanguage.set(language || 'en');
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: MouseEvent) {
    const clickedInside = this.elementRef.nativeElement.contains(event.target);
    if (!clickedInside) {
      this.isOpen = false;
    }
  }

  logout(){
    this.authService.logout().subscribe(() => {
        this.router.navigate(['/login']);
    });
  }

  toggleDropdown() {
    this.isOpen = !this.isOpen;
  }

  get hasProfile(): boolean {
    return !this.authService.isMinimalUser({ sub_type: this.userSubType });
  }

  navigateProfile(): void {
    this.isOpen = false;
    if (this.userSubType === 'ROWNER' || this.userSubType === 'RIDER') {
      this.router.navigate(['/store-profile']);
    } else {
      this.router.navigate(['/profile']);
    }
  }

  signOut(): void {
    this.isOpen = false;
    this.logout();
  }

  navigateHelp() {
    this.router.navigate(['/help']);
  }

  selectLanguage(language: string) {
    if (this.currentLanguage() === language) {
      return;
    }
    this.languageService.setLanguage(language);
    this.translationService.setLanguage(language);
    this.currentLanguage.set(language);
  }

  toggleSidebar(): void {
    this.commonService.sidebarOpen$.next(!this.commonService.sidebarOpen$.value);
  }
}
